import { useEffect } from 'react';
import type { JSX } from 'react';
import { Outlet, ScrollRestoration, useLocation } from 'react-router';
import { useAuthShell } from '@/auth/context';
import { shellIdentity } from '@/auth/identity';
import { useActivePersona, useTweaksStore } from '@/state/tweaksStore';
import { Toasts, TooltipLayer } from '../primitives';
import { Footer } from './Footer';
import { ModalHost } from './ModalHost';
import { Rail } from './Rail';
import { TopBar } from './TopBar';
import { Watermark } from './PageHeader';

/**
 * Application shell: navy rail, navy top bar, light canvas, dynamic-year footer.
 *
 * The grid areas are declared in base.css so the rail can be `position: sticky`
 * at full height while the main column scrolls independently.
 *
 * IDENTITY COMES FROM THE SIGNED-IN USER WHEN THERE IS ONE, else from the
 * active persona. In a build without login (`VITE_AUTH_PROVIDER=none`) there is
 * never a user, and who the shell presents as stays a tweak, so a presenter can
 * switch seats mid-demo and have the rail, the top bar and the account screen
 * move together. With a real login the account wins: a client must never see a
 * presenter's persona name in the top bar of their own session. The persona's
 * other effects (role, density) are untouched.
 */
export function AppShell(): JSX.Element {
  const persona = useActivePersona();
  const { user } = useAuthShell();
  const identity = shellIdentity(user, persona);
  const density = useTweaksStore((s) => s.density);
  const pinSidebar = useTweaksStore((s) => s.pinSidebar);
  const collapseProfileRail = useTweaksStore((s) => s.collapseProfileRail);
  const { pathname } = useLocation();

  // Layout tweaks are document-level: the rail and the footer sit outside
  // `.pnet-main`, so the attributes go on the root element and the CSS keys off
  // them. Writing them here keeps the shell markup free of tweak conditionals.
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.density = density;
    root.dataset.pinSidebar = String(pinSidebar);
    root.dataset.collapseProfileRail = String(collapseProfileRail);
  }, [density, pinSidebar, collapseProfileRail]);

  // Route changes should start at the top of the new screen, not wherever the
  // previous one was scrolled to.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <>
      <div className="pnet-app">
        <Rail userName={identity.displayName} />
        <TopBar initials={identity.initials} userName={identity.displayName} />
        <main className="pnet-main" id="main">
          <Outlet />
        </main>
        <Footer />
      </div>
      <Watermark />
      <Toasts />
      <TooltipLayer />
      <ModalHost />
      <ScrollRestoration />
    </>
  );
}
